import {
  DeleteOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { MemberPicker } from '@zhiyue/core';
import {
  Button,
  Card,
  Form,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { memberDirectoryAdapter } from '@/services/bff/member-directory.adapter';
import type { DataScopeGrant } from '@/services/greenplant';
import {
  getDataScopeGrants,
  revokeDataScopeGrant,
  upsertDataScopeGrant,
} from '@/services/greenplant';
import { userFacingErrorText } from '@/utils/userFacingError';

const RESOURCE_OPTIONS = [
  { label: '绿植工作台', value: 'greenplant-dashboard' },
  { label: '绿植台账', value: 'greenplant-assets' },
  { label: '养护任务', value: 'greenplant-tasks' },
  { label: '异常处理', value: 'greenplant-issues' },
  { label: '运营洞察', value: 'greenplant-insights' },
  { label: '配置中心', value: 'greenplant-settings' },
] as const;

const RESOURCE_NAMES = new Map(
  RESOURCE_OPTIONS.map((item) => [item.value, item.label]),
);

type GrantFormValues = {
  memberUuid: string | null;
  resourceCodes: string[];
};

const DataScopeManagement: React.FC = () => {
  const [form] = Form.useForm<GrantFormValues>();
  const [grants, setGrants] = React.useState<DataScopeGrant[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const loadGrants = React.useCallback(async () => {
    setLoading(true);
    try {
      const page = await getDataScopeGrants({ page: 1, pageSize: 100 });
      setGrants(page.list);
    } catch (error) {
      message.error(userFacingErrorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadGrants();
  }, [loadGrants]);

  const grantAllData = async (values: GrantFormValues) => {
    if (!values.memberUuid || values.resourceCodes.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        values.resourceCodes.map((resourceCode) =>
          upsertDataScopeGrant({
            subjectType: 'member',
            subjectUuid: values.memberUuid as string,
            resourceCode,
            scopeType: 'all',
            scopeKey: '*',
          }),
        ),
      );
      message.success('数据范围已授予');
      form.resetFields();
      await loadGrants();
    } catch (error) {
      message.error(userFacingErrorText(error));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (grant: DataScopeGrant) => {
    try {
      await revokeDataScopeGrant(grant.id);
      message.success('数据范围已撤销');
      await loadGrants();
    } catch (error) {
      message.error(userFacingErrorText(error));
    }
  };

  const columns: ColumnsType<DataScopeGrant> = [
    {
      title: '授权对象',
      dataIndex: 'subjectUuid',
      render: (value: string, record) => (
        <Space>
          <Tag color={record.subjectType === 'member' ? 'blue' : 'purple'}>
            {record.subjectType === 'member' ? '人员' : '组织'}
          </Tag>
          <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>
        </Space>
      ),
    },
    {
      title: '模块',
      dataIndex: 'resourceCode',
      render: (value: string) => RESOURCE_NAMES.get(value) ?? value,
    },
    {
      title: '数据范围',
      dataIndex: 'scopeType',
      render: (value: string, record) =>
        value === 'all' ? (
          <Tag color="green">全部数据</Tag>
        ) : (
          `${value}: ${record.scopeKey}`
        ),
    },
    {
      title: '状态',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean) =>
        active ? <Tag color="green">生效</Tag> : <Tag>已撤销</Tag>,
    },
    {
      title: '操作',
      width: 90,
      render: (_, record) => (
        <Popconfirm
          title="撤销该数据范围？"
          description="撤销后该人员将无法访问对应模块的数据。"
          okText="撤销"
          cancelText="取消"
          onConfirm={() => void revoke(record)}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            aria-label="撤销数据范围"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      size={16}
      style={{ display: 'flex', padding: 20 }}
    >
      <Card size="small" title="授予数据范围">
        <Typography.Paragraph type="secondary">
          先在“功能权限”中为角色分配模块权限，再在此为人员授予对应模块的数据访问范围。
        </Typography.Paragraph>
        <Form<GrantFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            memberUuid: null,
            resourceCodes: ['greenplant-assets'],
          }}
          onFinish={(values) => void grantAllData(values)}
        >
          <Form.Item
            label="人员"
            name="memberUuid"
            rules={[{ required: true, message: '请选择人员' }]}
          >
            <MemberPicker
              adapter={memberDirectoryAdapter}
              placeholder="搜索并选择企业成员"
            />
          </Form.Item>
          <Form.Item
            label="允许访问的模块"
            name="resourceCodes"
            rules={[{ required: true, message: '请选择至少一个模块' }]}
          >
            <Select
              mode="multiple"
              options={
                RESOURCE_OPTIONS as unknown as {
                  label: string;
                  value: string;
                }[]
              }
            />
          </Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            icon={<PlusOutlined />}
            loading={saving}
          >
            授予全部数据范围
          </Button>
        </Form>
      </Card>

      <Card
        size="small"
        title={
          <Space>
            <SafetyCertificateOutlined />
            已授权的数据范围
          </Space>
        }
        extra={
          <Button onClick={() => void loadGrants()} loading={loading}>
            刷新
          </Button>
        }
      >
        <Table<DataScopeGrant>
          rowKey="id"
          columns={columns}
          dataSource={grants}
          loading={loading}
          pagination={false}
          scroll={{ x: 760 }}
        />
      </Card>
    </Space>
  );
};

export default DataScopeManagement;
